import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import {
  getContactsRowsSelector,
  getContactsColsSelector,
  getContactsTotalSelector,
  getContactsPageSelector,
  getTempPageSelector,
  getExpandedListSelector,
  getEventsOptionSelector,
  getTempEditedFiltersSelector,
  getMassiveActionSelector,
  getExcludedContactsSelector,
  getTagEditorOpenedSelector,
  getExpandedIdSelector
} from '../contacts.reducer';
import {
  GetEventsAction,
  UpdateColumnsAction,
  SetFilterTagsAction,
  UpdateTempFilterMaxData,
  UpdateExpandedList,
  DeleteDataAction,
  SetMassiveOptionAction,
  SetExcludedContactsAction,
  SetCallFinishTypeFilter,
  UpdateTempEditedFilterList,
  ClearPageOffsetAction,
  SetPageOffsetAction
} from '../contacts.actions';
import { Contact, Column, Page, ExtraFilter } from '../shared/models/Contact';
import { ContactsService } from '../shared/services/contacts.service';
import * as moment from 'moment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ConfirmComponent } from '../../shared/confirm/confirm.component';
import { MatDialog } from '@angular/material/dialog';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FilterService } from '../shared/services/filter.service';
import { DATETIME_FORMATS } from '../../shared/utils/timeUtils';
import { DatePickerService } from '../../shared/services/date.picker.service';

@Component({
  selector: 'cc-contacts-table',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 0.9,
          height: '50px',
          padding: '1rem'
        })
      ),
      state(
        'closed',
        style({
          opacity: 0,
          height: 0,
          padding: 0
        })
      ),
      transition('open => closed', [animate('0.2s ease')]),
      transition('closed => open', [animate('0.1s ease')])
    ])
  ],
  templateUrl: './contacts-table.component.html',
  styleUrls: ['./contacts-table.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ContactsTableComponent implements OnInit {
  private toggleStop: boolean;
  private eventsLimiters = ['dateFrom', 'dateTo', 'users', 'eventTypes', 'callLengths', 'callFinishType'];
  public loadingIndicator: true;
  public reorderable: true;
  public expandedId: number;
  public selected = [];
  public swapColumns = false;
  public paginationOptions: number[] = [10, 20, 50, 100];
  public rows: Contact[];
  public columns: Column[];
  public total: number;
  public expandedList: number[];
  public page: Page;
  public tempPageFilters: Page;
  public dateDateView: string;
  public utcOffset = moment().utcOffset();
  public maxDate: moment.Moment | string;
  public auto: any;
  public emptyMessage: string;
  public messages: { emptyMessage: string };
  public activeEvent: number;
  private minDetailHeight = 530;
  private scrollOffset: number;
  public isEventsTable: boolean;
  public startDate: moment.Moment | string;
  public endDate: moment.Moment | string;
  private scrollWidth = 6;
  public filterParams: Page;
  public selectedToDelete: number[];
  public tempEditedFiltersList: string[];
  public tagEditorOpened = false;
  public isMassiveAction = false;
  public excludedContacts: number[] = [];
  public tableOffset: number;
  public isVoiceMailRecordingsFiltered = false;
  public initialRange = {
    startDate: moment()
      .subtract(7, 'day')
      .toISOString(),
    endDate: moment().toISOString()
  };
  @ViewChild('myTable') table: any;
  @ViewChild('openCalendar') iconCalendar: ElementRef;

  constructor(
    private store: Store<IState>,
    public contactsService: ContactsService,
    public datePickerService: DatePickerService,
    public translate: TranslateService,
    public dialog: MatDialog,
    public filterService: FilterService
  ) {}

  ngOnInit() {
    this.filterParams = {
      filter: {
        dateFrom: moment()
          .subtract(7, 'day')
          .startOf('day')
          .toISOString(),
        dateTo: moment()
          .toISOString(),
        contactId: null,
        users: []
      },
      pagination: {
        limit: 10,
        offset: 0,
        sortBy: null,
        order: null
      }
    };


    this.store.select(getContactsRowsSelector).subscribe((contacts: Contact[]) => {
      this.rows = contacts;
      if (this.isMassiveAction) {
        this.selected = this.rows.filter(row => !this.excludedContacts.includes(row.NATIVE_RECORD_ID));
      } else {
        this.selected = [];
      }
    });

    this.store.select(getContactsColsSelector).subscribe((columns: Column[]) => {
      this.columns = columns;
    });

    this.store.select(getContactsTotalSelector).subscribe((total: number) => {
      this.total = total;
    });

    this.store.select(getContactsPageSelector).subscribe((page: Page) => {
      this.page = page;
    });

    this.store.select(getExpandedListSelector).subscribe((expandedList: number[]) => {
      this.expandedList = expandedList;
    });

    this.store.select(getTempPageSelector).subscribe((tempPage: Page) => {
      this.tempPageFilters = tempPage;
      if (tempPage) {
        this.startDate = tempPage.filter ? tempPage.filter.dateFrom : '';
        this.endDate = tempPage.filter ? tempPage.filter.dateTo : '';
        this.filterParams = tempPage;
        this.dateDateView = this.getRangeTagName({
          dateFrom: this.filterParams.filter.dateFrom,
          dateTo: this.filterParams ? this.filterParams.filter.dateTo : moment().toISOString()
        });
        this.tableOffset = this.filterParams.pagination.offset / this.filterParams.pagination.limit;
      }
      this.isVoiceMailRecordingsFiltered = tempPage.filter.callFinishType === 'INCOMING_CALL_VOICEMAIL';
    });

    this.contactsService.fetchDataAction(this.filterParams);

    this.contactsService.useFilters.subscribe(() => {
      this.onUseFilters();
    });

    this.store.select(getTagEditorOpenedSelector).subscribe(isTagEditorOpened => {
      this.tagEditorOpened = isTagEditorOpened;
    });

    this.translate.get('CONTACTS_LIST.NO_DATA_FOUND').subscribe(text => {
      this.emptyMessage = text;
      this.messages = {
        emptyMessage: `<div class="empty-message"><span>${this.emptyMessage}</span></div>`
      };
    });

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.messages = {
        emptyMessage: `<div class="empty-message"><span>${event.translations.CONTACTS_LIST.NO_DATA_FOUND}</span></div>`
      };
    });

    this.store.select(getEventsOptionSelector).subscribe((isEventsTable: boolean) => {
      this.isEventsTable = isEventsTable ? isEventsTable : false;
    });

    this.contactsService.onRowCollapse.subscribe(() => {
      this.table.rowDetail.collapseAllRows();
    });

    this.contactsService.onFilterExtraBy.subscribe((extraFilter: ExtraFilter) => {
      if (extraFilter) {
        this.acceptExtraFilter(extraFilter);
        this.contactsService.fetchDataAction(this.filterParams, 0);
      }
    });

    this.store.select(getTempEditedFiltersSelector).subscribe((tempEditedFiltersList: string[]) => {
      this.tempEditedFiltersList = tempEditedFiltersList;
    });

    this.store.select(getMassiveActionSelector).subscribe((massAction: boolean) => {
      this.isMassiveAction = massAction;
      if (this.isMassiveAction) {
        this.selected = this.selected || this.rows;
      }
    });

    this.store.select(getExcludedContactsSelector).subscribe((excluded: number[]) => {
      this.excludedContacts = excluded;
    });

    this.store.select(getExpandedIdSelector).subscribe((expandedId: number) => {
      this.expandedId = expandedId;
    });

    this.maxDate = moment().add(this.utcOffset / 60, 'hours').toISOString();
  }

  shouldDisplayEventResultInfo() {
    const limiters = this.tempEditedFiltersList.filter(filter => this.eventsLimiters.includes(filter));

    return (
      (limiters.length || !this.isDateRangeDefault()) && this.expandedList.length && this.expandedId && this.total > 0
    );
  }

  isDateRangeDefault() {
    return (
      moment(this.startDate).diff(this.initialRange.startDate, 'days') === 0 &&
      moment(this.endDate).diff(this.initialRange.endDate, 'minutes') === 0
    );
  }

  acceptExtraFilter(extraFilter: ExtraFilter) {
    Object.keys(extraFilter).forEach(key => {
      const newTempEditedFiltersList = this.filterService.getEditedFiltersListUpdate(
        key,
        this.tempEditedFiltersList ? this.tempEditedFiltersList : []
      );
      if (key === 'callFinishType') {
        this.store.dispatch(new SetCallFinishTypeFilter(extraFilter[key]));
      }
      this.store.dispatch(new UpdateTempEditedFilterList(newTempEditedFiltersList));
    });
  }

  onDomChange($event: Event): void {
    this.resizeColumns();
  }

  resizeColumns() {
    const sourceColumnHeader = Array.from(document.querySelectorAll('.datatable-header-inner'));
    const sourceColumnHeaderCells =
      sourceColumnHeader && sourceColumnHeader.length
        ? Array.from(sourceColumnHeader[0].querySelectorAll('.datatable-row-center .datatable-header-cell'))
        : [];
    const targetColumnBodyCells = Array.from(
      document.querySelectorAll('.datatable-row-detail .datatable-row-center .datatable-body-cell')
    );
    const targetColumnHeaderCells = Array.from(
      document.querySelectorAll('.datatable-row-detail .datatable-row-center .datatable-header-cell')
    );
    const sourceColeWidthNames = sourceColumnHeaderCells.map((col, index) => ({
      // @ts-ignore
      name: col.innerText,
      // @ts-ignore
      width: col.offsetWidth,
      index: index
    }));

    sourceColeWidthNames.forEach((source, indexSource) => {
      this.correctCellsWidth(targetColumnHeaderCells, sourceColeWidthNames, source, indexSource);
      this.correctCellsWidth(targetColumnBodyCells, sourceColeWidthNames, source, indexSource);
    });

    this.onScroll({ offsetX: this.scrollOffset });
  }

  correctCellsWidth(targetCollection, sourceHeaderCollection, sourceElement, sourceIndex) {
    targetCollection.forEach((_, index) => {
      const searchIndex = index * sourceHeaderCollection.length + sourceIndex;
      if (searchIndex < targetCollection.length) {
        targetCollection[searchIndex].setAttribute('style', `width: ${sourceElement.width}px; margin-right: 1px;`);
      }
    });
  }

  onActivate(event) {
    this.activeEvent = event.row.event_id;
  }

  isRowExpanded(row) {
    return (row.events && row.events.length) || this.expandedList.includes(this.getRowIdentity(row));
  }

  toggleExpandRow(event) {
    const eventId = event.row;

    if (event.type === 'click') {
      const checkbox = event.cellElement.firstChild.firstElementChild.classList;
      this.toggleStop = checkbox[0] === 'datatable-checkbox';

      if (!this.toggleStop) {
        if (!this.isRowExpanded(eventId)) {
          this.expandedList.push(this.getRowIdentity(eventId));
          this.store.dispatch(new UpdateExpandedList(this.expandedList));
        }
        this.table.rowDetail.toggleExpandRow(eventId);
      }
    }
  }

  onDetailToggle(event) {
    this.filterParams.filter.contactId = event.value['NATIVE_RECORD_ID'];

    if (!this.isEventsTable && event.value) {
      this.store.dispatch(new GetEventsAction(this.filterParams));
    }
  }

  getRowIdentity(row) {
    return row.NATIVE_RECORD_ID;
  }

  onSelect(event) {
    this.selected = event.selected;
    this.selectedToDelete = this.selected.map(item => item.NATIVE_RECORD_ID);
    this.excludedContacts = this.rows
      .map(item => item.NATIVE_RECORD_ID)
      .filter(id => !this.selectedToDelete.includes(id));

    this.contactsService.addParamsToContacts({
      includedToTag: this.selectedToDelete,
      excludedFromTag: this.excludedContacts
    });
  }

  toggleOpenTagEditor() {
    this.contactsService.toggleTagsEditor(!this.tagEditorOpened);

    if (!!this.tagEditorOpened) {
      this.contactsService.addParamsToContacts({
        includedToTag: this.selected.map(item => item.NATIVE_RECORD_ID),
        excludedFromTag: this.excludedContacts
      });
    }
  }

  deleteSelected() {
    this.selectedToDelete = this.selected.map(item => item.NATIVE_RECORD_ID);
    if (this.isMassiveAction) {
      this.excludedContacts = this.rows
        .map(item => item.NATIVE_RECORD_ID)
        .filter(id => !this.selectedToDelete.includes(id));
      this.store.dispatch(new DeleteDataAction({ exclude: this.excludedContacts }));
    } else {
      this.store.dispatch(new DeleteDataAction({ include: this.selectedToDelete }));
    }
    this.selected = [];
  }

  confirmToDelete() {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: 'auto',
      height: 'auto',
      data: {
        title:
          this.selected && this.selected.length === 1
            ? 'CONTACTS_LIST.ON_DELETE_ONE_TITLE'
            : 'CONTACTS_LIST.ON_DELETE_TITLE',
        description: 'CONTACTS_LIST.ON_DELETE_DESCRIPTION',
        type: 'strong'
      },
      panelClass: 'confirm-dialog-wrapper'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'strong') {
        this.deleteSelected();
      }
    });
  }

  onSort(event) {
    this.table.rowDetail.collapseAllRows();

    const colName = event.sorts[0].prop.startsWith('NATIVE')
      ? event.sorts[0].prop.replace('NATIVE_', '')
      : event.sorts[0].prop.startsWith('IMPORTED') ? event.sorts[0].prop.replace('IMPORTED_', '') : event.sorts[0].prop;

    this.filterParams.pagination.sortBy =
      colName === 'DETAILS' ? 'NAME' : colName === 'AGENT' ? 'CREATOR_LOGIN' : colName;
    this.filterParams.pagination.order = event.sorts[0].dir.toUpperCase();
    this.filterParams.pagination.offset = 0;
    this.contactsService.fetchDataAction(this.filterParams);
  }

  toggleChecking(col) {
    const isChecked = this.isChecked(col);

    if (isChecked) {
      this.columns = this.columns.filter(c => {
        return c.key !== col.key;
      });
    } else {
      this.columns = [...this.columns, col];
    }

    this.store.dispatch(new UpdateColumnsAction(this.columns));
  }

  isChecked(col) {
    return this.columns.find(c => {
      return c.key === col.key;
    });
  }

  setPage(pageInfo) {
    this.filterParams.pagination.offset = pageInfo.offset * this.filterParams.pagination.limit;
    this.contactsService.fetchDataAction(this.filterParams);
  }

  changePagination(event) {
    this.filterParams.pagination.offset = 0;
    this.filterParams.pagination.limit = event.value;
    this.contactsService.fetchDataAction(this.filterParams);
    this.table.rowDetail.collapseAllRows();
  }

  onUseFilters() {
    this.store.dispatch(new ClearPageOffsetAction(0));
    this.store.dispatch(new SetFilterTagsAction());
    this.contactsService.fetchDataAction(this.filterParams, 0);
    this.table.rowDetail.collapseAllRows();
  }

  getRangeTagName(rangeFilter: { dateFrom: moment.Moment | string; dateTo: moment.Moment | string }) {
    const dateFrom = `${moment(rangeFilter.dateFrom).format('L')}`;
    const dateTo = `${moment(rangeFilter.dateTo).format('L')}`;

    return dateFrom === dateTo ? dateTo : `${dateFrom} - ${dateTo}`;
  }

  onScroll($event) {
    const detailRow = Array.from(document.querySelectorAll('.detail-inner-row'));
    detailRow.forEach(detail => {
      const detailBodyRowLeftColumn = Array.from(detail.querySelectorAll('.datatable-row-left'));
      const tableBody = detail.querySelectorAll('.datatable-body')[0].clientHeight;
      const widthColCorrection = tableBody >= this.minDetailHeight ? this.scrollWidth : 0;
      const headerCell = detail.querySelectorAll('.datatable-row-left .datatable-header-cell:nth-child(2)')[0];

      detailBodyRowLeftColumn.forEach(element => {
        element.setAttribute('style', `transform: translate3d(${$event.offsetX - widthColCorrection}px, 0px, 0px);`);
      });

      if (tableBody >= this.minDetailHeight) {
        headerCell.classList.add('class', 'scroll-corrected');
      } else {
        headerCell.classList.remove('scroll-corrected');
      }
    });

    this.scrollOffset = $event.offsetX;
  }

  getDateFormated(date: string) {
    return moment(date).format(DATETIME_FORMATS.us);
  }

  getEventTypeLabel(statusName: string) {
    return this.contactsService.getEventTypeLabel(statusName);
  }

  getCallLengthLabel(callLength: number) {
    const minutes = Math.floor(callLength / 60);
    const seconds = callLength - minutes * 60;
    return minutes === 0 && seconds === 0 ? '-' : `${minutes}min ${seconds}sec`;
  }

  buttonClick(event) {
    //future feature event
  }

  openPicker() {
    this.datePickerService.toggleDatePicker({
      open: 'right',
      show: true,
      positionX: this.iconCalendar.nativeElement.offsetLeft,
      positionY: this.iconCalendar.nativeElement.offsetTop
    });
  }

  addFilter() {
    this.contactsService.showFilters();
    this.contactsService.showSearchFilters();
    this.contactsService.snapshotCallFinishType();
  }

  openEditorTag($contact, $event) {
    $event.stopPropagation();
    this.contactsService.toggleShowRowTagsEditor(true);
    this.contactsService.setTagEditorPosition({
      top: $event.clientY,
      left: $event.clientX,
      rowId: $contact.NATIVE_RECORD_ID
    });
  }

  toggleMassiveOptionAction() {
    if (!this.isMassiveAction) {
      this.selected = this.rows;
      this.excludedContacts = [];
    }
    this.store.dispatch(new SetMassiveOptionAction(!this.isMassiveAction));
    this.store.dispatch(new SetExcludedContactsAction(this.excludedContacts));
    this.contactsService.fetchDataAction(this.filterParams);
  }

  rememberPaginationOffset() {
    this.store.dispatch(new SetPageOffsetAction(this.table.offset * this.table.limit));
  }
}
